import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  GetApp as GetAppIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { getWorkers, getVacationRequests, getWorkCenters, getHolidays, exportVacationsToExcel, getMyVacationSummary } from '../services/api';
import { VacationStatus, Worker } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalWorkers: number;
  pendingRequests: number;
  totalWorkCenters: number;
  upcomingHolidays: number;
}

interface VacationSummary {
  current_year: number;
  current_year_days: {
    total: number;
    used: number;
    remaining: number;
  };
  flexible_hours: {
    total: number;
    used: number;
    remaining: number;
  };
  previous_year?: {
    year: number;
    total: number;
    used: number;
    remaining: number;
  };
}

const Dashboard: React.FC = () => {
  const { isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalWorkers: 0,
    pendingRequests: 0,
    totalWorkCenters: 0,
    upcomingHolidays: 0
  });
  const [vacationSummary, setVacationSummary] = useState<VacationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportYear, setExportYear] = useState<number>(new Date().getFullYear());
  const [exportWorker, setExportWorker] = useState<number | ''>('');
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (isAdmin) {
          // Admin users: load all stats
          const [workersData, requests, workCenters, holidays, summary] = await Promise.all([
            getWorkers(),
            getVacationRequests(),
            getWorkCenters(),
            getHolidays(undefined, new Date().getFullYear()),
            getMyVacationSummary()
          ]);

          const pendingRequests = requests.filter(r => r.status === VacationStatus.PENDING).length;
          const currentDate = new Date();
          const upcomingHolidays = holidays.filter(h => new Date(h.date) > currentDate).length;

          setWorkers(workersData);
          setVacationSummary(summary);
          setStats({
            totalWorkers: workersData.length,
            pendingRequests,
            totalWorkCenters: workCenters.length,
            upcomingHolidays
          });
        } else {
          // Normal users: only load vacation summary
          const summary = await getMyVacationSummary();
          setVacationSummary(summary);
        }
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [isAdmin]);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const blob = await exportVacationsToExcel(
        exportYear,
        exportWorker || undefined
      );
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const filename = exportWorker 
        ? `vacaciones_${workers.find(w => w.id === exportWorker)?.name}_${exportYear}.xlsx`
        : `vacaciones_${exportYear}.xlsx`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting Excel:', error);
    } finally {
      setExportLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Trabajadores',
      value: stats.totalWorkers,
      icon: <PeopleIcon sx={{ fontSize: 20, color: '#001923' }} />,
      path: '/workers',
    },
    {
      title: 'Pendientes',
      value: stats.pendingRequests,
      icon: <EventNoteIcon sx={{ fontSize: 20, color: '#001923' }} />,
      path: '/vacation-requests',
    },
    {
      title: 'Centros',
      value: stats.totalWorkCenters,
      icon: <BusinessIcon sx={{ fontSize: 20, color: '#001923' }} />,
      path: '/work-centers',
    },
    {
      title: 'Festivos',
      value: stats.upcomingHolidays,
      icon: <EventIcon sx={{ fontSize: 20, color: '#001923' }} />,
      path: '/holidays',
    }
  ];

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <Typography 
          sx={{ 
            color: '#666666',
            fontWeight: 300,
            fontSize: '0.9rem'
          }}
        >
          Cargando...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0 }}>
      <Typography 
        variant="h5" 
        sx={{ 
          mb: 1,
          fontWeight: 700, // Negrita
          color: '#001923',
          letterSpacing: '-0.01em'
        }}
      >
        Dashboard
      </Typography>
      <Typography 
        variant="body1" 
        className="descripcion-cyan"
        sx={{ 
          mb: { xs: 3, sm: 6 },
          fontWeight: 300,
          lineHeight: 1.6
        }}
      >
        Resumen del sistema de gestión
      </Typography>
      
      {isAdmin && (
        <Grid container spacing={{ xs: 2, sm: 4 }} sx={{ mb: { xs: 4, sm: 8 } }}>
          {statCards.map((card, index) => (
            <Grid item xs={6} sm={6} md={3} key={index}>
              <Paper 
                elevation={0}
                onClick={() => navigate(card.path)}
                sx={{ 
                  p: { xs: 2, sm: 4 },
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #f0f0f0',
                  borderRadius: 0,
                  cursor: 'pointer',
                  '@media (hover: hover)': {
                    '&:hover': {
                      borderColor: '#00B0BD',
                      transition: 'border-color 0.2s ease',
                    }
                  },
                  '&:active': { borderColor: '#00B0BD' },
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#001923',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                  >
                    {card.title}
                  </Typography>
                  {card.icon}
                </Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: '#001923',
                    fontWeight: 300,
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                    lineHeight: 1
                  }}
                >
                  {card.value}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {vacationSummary && (
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, sm: 6 },
            backgroundColor: '#FFFFFF',
            border: '1px solid #f0f0f0',
            borderRadius: 0,
            mb: { xs: 4, sm: 8 }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: { xs: 2, sm: 4 },
              fontWeight: 400,
              color: '#001923',
              fontSize: '1.1rem'
            }}
          >
            Mi Resumen de Vacaciones
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            {vacationSummary.previous_year && (
              <Grid item xs={12} md={4}>
                <Box 
                  sx={{ 
                    p: { xs: 2, sm: 4 }, 
                    border: '1px solid #f0f0f0',
                    borderRadius: 0,
                    backgroundColor: '#FAFAFA',
                    height: '100%',
                    minHeight: { xs: '160px', sm: '220px' },
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                    <CalendarIcon sx={{ fontSize: 20, color: '#004254', mr: 1.5 }} />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#004254',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        fontSize: '11px',
                        fontWeight: 500
                      }}
                    >
                      Año Anterior ({vacationSummary.previous_year.year})
                    </Typography>
                  </Box>
                  <Typography 
                    variant="h3" 
                    sx={{ 
                      color: '#001923',
                      fontWeight: 300,
                      fontSize: { xs: '1.8rem', sm: '2.5rem' },
                      lineHeight: 1,
                      mb: 1
                    }}
                  >
                    {vacationSummary.previous_year.remaining}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#666666',
                      fontSize: '0.85rem',
                      mt: 2
                    }}
                  >
                    Usados: {vacationSummary.previous_year.used} / {vacationSummary.previous_year.total} días
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#999999',
                      fontSize: '0.75rem',
                      mt: 1,
                      display: 'block'
                    }}
                  >
                    Disponibles hasta el 31 de Marzo
                  </Typography>
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12} md={vacationSummary.previous_year ? 4 : 6}>
              <Box 
                sx={{ 
                  p: { xs: 2, sm: 4 }, 
                  border: '2px solid #00B0BD',
                  borderRadius: 0,
                  backgroundColor: '#FFFFFF',
                  height: '100%',
                  minHeight: { xs: '160px', sm: '220px' },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <CalendarIcon sx={{ fontSize: 20, color: '#00B0BD', mr: 1.5 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#00B0BD',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                  >
                    Año Actual ({vacationSummary.current_year})
                  </Typography>
                </Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: '#001923',
                    fontWeight: 300,
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  {vacationSummary.current_year_days.remaining}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666666',
                    fontSize: '0.85rem',
                    mt: 2
                  }}
                >
                  Usados: {vacationSummary.current_year_days.used} / {vacationSummary.current_year_days.total} días
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={vacationSummary.previous_year ? 4 : 6}>
              <Box 
                sx={{ 
                  p: { xs: 2, sm: 4 }, 
                  border: '1px solid #f0f0f0',
                  borderRadius: 0,
                  backgroundColor: '#FFFFFF',
                  height: '100%',
                  minHeight: { xs: '160px', sm: '220px' },
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                  <AccessTimeIcon sx={{ fontSize: 20, color: '#004254', mr: 1.5 }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#004254',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      fontSize: '11px',
                      fontWeight: 500
                    }}
                  >
                    Horas Flexibles
                  </Typography>
                </Box>
                <Typography 
                  variant="h3" 
                  sx={{ 
                    color: '#001923',
                    fontWeight: 300,
                    fontSize: { xs: '1.8rem', sm: '2.5rem' },
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  {vacationSummary.flexible_hours.remaining.toFixed(1)}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#666666',
                    fontSize: '0.85rem',
                    mt: 2
                  }}
                >
                  Usadas: {vacationSummary.flexible_hours.used.toFixed(1)} / {vacationSummary.flexible_hours.total} horas
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {isAdmin && (
        <Paper 
          elevation={0}
          sx={{ 
            p: { xs: 3, sm: 6 },
            backgroundColor: '#FFFFFF',
            border: '1px solid #f0f0f0',
            borderRadius: 0,
            mb: { xs: 4, sm: 8 }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3,
              fontWeight: 400,
              color: '#001923',
              fontSize: '1.1rem'
            }}
          >
            Utilidades
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={() => setExportDialogOpen(true)}
              sx={{ 
                borderColor: '#e0e0e0',
                color: '#1a1a1a',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#c0c0c0',
                  backgroundColor: '#f9f9f9'
                }
              }}
            >
              Exportar a Excel
            </Button>
          </Box>
        </Paper>
      )}

      <Paper 
        elevation={0}
        sx={{ 
          p: { xs: 3, sm: 6 },
          backgroundColor: '#FFFFFF',
          border: '1px solid #f0f0f0',
          borderRadius: 0
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 3,
            fontWeight: 400,
            color: '#001923',
            fontSize: '1.1rem'
          }}
        >
          Accesos
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          <Box component="li" sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#1a1a1a',
                fontWeight: 300,
                lineHeight: 1.6
              }}
            >
              Calendario Mensual — Análisis visual de cobertura del equipo
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#1a1a1a',
                fontWeight: 300,
                lineHeight: 1.6
              }}
            >
              Solicitudes — Gestión de peticiones de vacaciones
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#1a1a1a',
                fontWeight: 300,
                lineHeight: 1.6
              }}
            >
              Trabajadores — Administración de empleados y centros
            </Typography>
          </Box>
          <Box component="li">
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#1a1a1a',
                fontWeight: 300,
                lineHeight: 1.6
              }}
            >
              Festivos — Configuración de calendarios por centro
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Exportar Vacaciones a Excel</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <TextField
              label="Año"
              type="number"
              value={exportYear}
              onChange={(e) => setExportYear(parseInt(e.target.value))}
              inputProps={{ min: 2020, max: 2030 }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Trabajador (opcional)</InputLabel>
              <Select
                value={exportWorker}
                onChange={(e) => setExportWorker(e.target.value as number | '')}
                label="Trabajador (opcional)"
              >
                <MenuItem value="">Todos los trabajadores</MenuItem>
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {worker.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setExportDialogOpen(false)}
            disabled={exportLoading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleExportExcel}
            disabled={exportLoading}
            variant="contained"
            startIcon={<GetAppIcon />}
          >
            {exportLoading ? 'Exportando...' : 'Exportar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;